import React from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell
} from 'recharts'
import { Card, CardContent, CardHeader } from '../ui'
import { useI18n } from '../../i18n'
import { componentScope } from '../../lib/cssScope'

interface SupplierCriticalityData {
  supplierId: string
  supplierName: string
  criticalityScore: number
  criticalityLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
}

interface SuppliersCriticalityDistributionChartProps {
  data: SupplierCriticalityData[]
}

const SuppliersCriticalityDistributionChart: React.FC<SuppliersCriticalityDistributionChartProps> = ({ data }) => {
  const { t } = useI18n()

  // Sort by criticality score ascending (MIN → MAX)
  const sortedData = [...data].sort((a, b) => a.criticalityScore - b.criticalityScore)

  // Color mapping based on criticality level
  const getColor = (level: string) => {
    switch (level) {
      case 'LOW':
        return '#10b981' // green
      case 'MEDIUM':
        return '#eab308' // yellow
      case 'HIGH':
        return '#f59e0b' // orange
      case 'CRITICAL':
        return '#ef4444' // red
      default:
        return '#9ca3af' // gray
    }
  }

  const chartData = sortedData.map(item => ({
    name: item.supplierName,
    score: item.criticalityScore,
    level: item.criticalityLevel,
    fill: getColor(item.criticalityLevel)
  }))

  return (
    <Card className={`${componentScope('SuppliersCriticalityDistributionChart')} metric-card`}>
      <CardHeader>
        <div className="text-lg font-semibold">
          {t('tpsm.charts.criticalityDistribution.title', 'Distribution of Suppliers by Criticality Level')}
        </div>
        <div className="text-sm text-gray-500">
          {t('tpsm.charts.criticalityDistribution.subtitle', 'Suppliers sorted by criticality score (lowest to highest)')}
        </div>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-500">
            <div className="text-center">
              <div className="text-sm">{t('common.noData', 'No data available')}</div>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart 
              data={chartData} 
              margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                type="category"
                dataKey="name"
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={100}
              />
              <YAxis 
                type="number"
                tick={{ fontSize: 12 }}
                label={{ value: t('tpsm.charts.criticalityDistribution.score', 'Criticality Score'), angle: -90, position: 'insideLeft' }}
              />
              <Tooltip
                formatter={(value: number, name: string, props: any) => {
                  const payload = props.payload
                  return [
                    `${value.toFixed(2)} (${payload.level})`,
                    t('tpsm.charts.criticalityDistribution.score', 'Criticality Score')
                  ]
                }}
                labelFormatter={(label) => t('tpsm.supplier', 'Supplier') + ': ' + label}
              />
              <Legend />
              <Bar 
                dataKey="score" 
                name={t('tpsm.charts.criticalityDistribution.score', 'Criticality Score')}
                radius={[4, 4, 0, 0]}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}

export default SuppliersCriticalityDistributionChart

